import { Coin } from './Coin';
import { JSONSerializable } from '../util/json';
import { Denom } from './Denom';
import { Numeric } from './numeric';

export class Coins extends JSONSerializable<Coins.Data>
  implements Numeric<Coins> {
  private _coins: Coins.ReprDict;

  public toString(): string {
    return this.toArray()
      .map(c => c.toString())
      .join(', ');
  }

  public toDecCoins(): Coins {
    return new Coins(this.map(c => c.toDecCoin()));
  }

  public toIntCoins(): Coins {
    return new Coins(this.map(c => c.toIntCoin()));
  }

  constructor(arg: Coins.Input) {
    super();
    if (arg instanceof Coins) {
      this._coins = { ...arg._coins };
    } else {
      this._coins = {};
      let coins: Coin[];
      if (!Array.isArray(arg)) {
        coins = [];
        Object.keys(arg).forEach(denom =>
          coins.push(new Coin(denom, arg[denom]))
        );
      } else {
        coins = arg;
      }

      for (const coin of coins) {
        const { denom } = coin;
        const x = this._coins[denom];
        if (x !== undefined) {
          this._coins[denom] = x.add(coin);
        } else {
          this._coins[denom] = coin;
        }
      }

      if (
        !this.toArray().every(c => c.isDecCoin()) &&
        !this.toArray().every(c => c.isIntCoin())
      ) {
        throw new Error('non-homogenous instantiation of Coins not supported');
      }
    }
  }

  public get(denom: Denom): Coin | undefined {
    return this._coins[denom];
  }

  public set(denom: Denom, value: Numeric.Input | Coin): void {
    let val;
    if (value instanceof Coin) {
      if (value.denom != denom) {
        throw new Error(
          `Denoms must match when setting: ${denom}, ${value.denom}`
        );
      }
      val = value;
    } else {
      val = new Coin(denom, value);
    }
    this._coins[denom] = val;
  }

  public static fromData(data: Coins.Data): Coins {
    return new Coins(data.map(Coin.fromData));
  }

  public toArray(): Coin[] {
    return Object.values(this._coins).sort((a, b) =>
      a.denom.localeCompare(b.denom)
    );
  }

  public toData(): Coins.Data {
    return this.toArray().map(c => c.toData());
  }

  public add(other: Coin | Coins): Coins {
    if (other instanceof Coin) {
      return new Coins([other, ...Object.values(this._coins)]);
    } else {
      return new Coins([
        ...Object.values(other._coins),
        ...Object.values(this._coins),
      ]);
    }
  }

  public sub(other: Coin | Coins): Coins {
    return this.add(other.mul(-1));
  }

  public mul(other: Numeric.Input): Coins {
    return new Coins(this.map(c => c.mul(other)));
  }

  public div(other: Numeric.Input): Coins {
    return new Coins(this.map(c => c.div(other)));
  }

  public mod(other: Numeric.Input): Coins {
    return new Coins(this.map(c => c.mod(other)));
  }

  public map<T>(fn: (c: Coin) => T): T[] {
    return this.toArray().map(fn);
  }

  public filter(fn: (c: Coin) => boolean): Coins {
    return new Coins(this.toArray().filter(fn));
  }
}

export namespace Coins {
  export type Input = Coins.DataDict | Coin[] | Coins;
  export type Data = Coin.Data[];
  export type DataDict = {
    [denom: string]: Numeric.Input;
  };
  export type ReprDict = {
    [denom: string]: Coin;
  };
}
